/**
 * PortMapModal — Port connectivity editor
 * Handles Patch Panels, Switches.
 * Supports: element-to-element (with port selector), wall outlet selector, uplink (free text).
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';
import { vlanChip, vlanTitle } from '../utils/portVlan';
import {
  buildLocationMap,
  groupElementsByLocation,
  locationRootId,
} from '../utils/locationTree';
import './PortMapModal.css';

const CONNECTOR_TYPES = ['rj45', 'lc', 'st', 'sc', 'mtp', 'sfp', 'sfp+', 'qsfp'];
const CABLE_TYPES     = ['', 'cat5e', 'cat6', 'cat6a', 'cat7', 'fiber_om3', 'fiber_om4', 'fiber_sm', 'dac'];
const CABLE_COLORS    = ['', 'azul', 'rojo', 'verde', 'amarillo', 'naranja', 'morado', 'gris', 'blanco', 'negro'];
const SWITCH_SPEEDS   = ['', '100M', '1G', '2.5G', '10G', '25G', '40G', '100G'];
const EMPTY_FORM = {
  destination_type:      '',
  destination_outlet_id: '',
  destination_element_id: '',
  destination_port_id:   '',
  uplink_text:           '',
  connection_type:       'rj45',
  cable_type:            '',
  cable_color:           '',
  cable_length_m:        '',
  notes:                 '',
  vlan:                  '',
  speed:                 '',
};

function PortMapModal({ element, rackElements, companyId, onClose, onUpdated, onUpdate }) {
  const [ports,         setPorts]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedPort,  setSelectedPort]  = useState(null);
  const [editForm,      setEditForm]      = useState(EMPTY_FORM);
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState('');
  const [destPorts,        setDestPorts]        = useState([]);
  const [loadingDestPorts, setLoadingDestPorts] = useState(false);
  const [wallOutlets,        setWallOutlets]        = useState([]);
  const [loadingWallOutlets, setLoadingWallOutlets] = useState(false);
  const [locations,          setLocations]          = useState([]);
  const [companyElements,    setCompanyElements]    = useState(null);
  const [showAllLocations,   setShowAllLocations]   = useState(false);

  const isSwitch = element?.element_type === 'switch';
  const isIPPhone = element?.element_type === 'ip_phone';

  const fetchPorts = useCallback(async (forceApi = false) => {
    if (!element?.id) return;
    // For IP phones: use element.ports on initial load, but refetch from API when forced (after save)
    if (isIPPhone && element.ports && !forceApi) {
      setPorts(element.ports);
      setLoading(false);
      return;
    }
    if (isIPPhone) {
      try {
        setLoading(true);
        const data = await api.getIPPhone(element.id, companyId);
        setPorts((data.data || data)?.ports || element.ports || []);
      } catch (err) {
        console.error('Error loading IP phone ports:', err);
        setPorts(element.ports || []);
      } finally { setLoading(false); }
      return;
    }
    try {
      setLoading(true);
      const data = await api.getPorts(element.id, companyId);
      setPorts(data.data || []);
    } catch (err) {
      console.error('Error loading ports:', err);
      setPorts([]);
    } finally { setLoading(false); }
  }, [element?.id, companyId]);

  const notifyUpdated = useCallback(() => {
    if (onUpdated) onUpdated();
    if (onUpdate) onUpdate();
  }, [onUpdated, onUpdate]);

  useEffect(() => { fetchPorts(); }, [fetchPorts]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // Load destination element ports when element selector changes
  useEffect(() => {
    if (editForm.destination_element_id) {
      setLoadingDestPorts(true);
      api.getPorts(parseInt(editForm.destination_element_id), companyId)
        .then(r => setDestPorts(r.data || []))
        .catch(() => setDestPorts([]))
        .finally(() => setLoadingDestPorts(false));
    } else { setDestPorts([]); }
  }, [editForm.destination_element_id, companyId]);

  // Load wall outlets lazily when wall type is chosen
  useEffect(() => {
    if (editForm.destination_type === 'wall' && wallOutlets.length === 0 && !loadingWallOutlets) {
      setLoadingWallOutlets(true);
      api.getWallOutlets(companyId)
        .then(r => setWallOutlets(r.data || []))
        .catch(() => setWallOutlets([]))
        .finally(() => setLoadingWallOutlets(false));
    }
  }, [editForm.destination_type, companyId, wallOutlets.length, loadingWallOutlets]);

  // Árbol de ubicaciones para no limitar los destinos al rack del elemento:
  // se muestran los equipos de la misma raíz (sede) y, si se marca el check,
  // también los de otras ubicaciones.
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId, { includeInactive: true })
      .then(r => setLocations(r.data || []))
      .catch(() => setLocations([]));
  }, [companyId]);

  const locationMap = useMemo(() => buildLocationMap(locations), [locations]);
  const currentRootId = useMemo(
    () => locationRootId(locationMap, element?.location_id),
    [locationMap, element?.location_id],
  );

  // Si el llamador trabaja con destinos de rack, el modal carga el catálogo
  // completo de la empresa (no solo el rack actual) para poder cruzar plantas.
  const rackDestinationMode = Array.isArray(rackElements);
  useEffect(() => {
    if (!rackDestinationMode || !companyId) {
      setCompanyElements(null);
      return undefined;
    }
    let cancelled = false;
    api.getElements(companyId)
      .then(r => { if (!cancelled) setCompanyElements(r.data || []); })
      .catch(() => { if (!cancelled) setCompanyElements(null); });
    return () => { cancelled = true; };
  }, [rackDestinationMode, companyId]);

  const selectedElementId = useMemo(() => {
    const raw = editForm.destination_element_id;
    return raw ? String(raw) : null;
  }, [editForm.destination_element_id]);

  const otherElements = useMemo(() => {
    const base = (companyElements || rackElements || []).filter(e => e.id !== element?.id);
    if (showAllLocations || !currentRootId) return base;
    return base.filter(e => (
      locationRootId(locationMap, e.location_id) === currentRootId
      // La conexión ya guardada se mantiene visible aunque sea de otra raíz.
      || (selectedElementId != null && String(e.id) === selectedElementId)
    ));
  }, [companyElements, rackElements, element?.id, showAllLocations, currentRootId, locationMap, selectedElementId]);

  const otherElementGroups = useMemo(
    () => groupElementsByLocation(locationMap, otherElements),
    [locationMap, otherElements],
  );

  // ── Populate form from port on click ──────────────────────────────────

  const handlePortClick = (port) => {
    setSelectedPort(port);
    setSaveError('');
    setDestPorts([]);
    const conn = port.connection;
    let dest_type = '', outlet_id = '', elem_id = '', port_id = '', uplink_txt = '';

    if (conn) {
      const other = conn.other_end;
      if (other.port_type === 'wall_outlet') {
        dest_type = 'wall'; outlet_id = String(other.port_id);
      } else {
        dest_type = 'element';
        elem_id   = String(other.element_id || '');
        port_id   = String(other.port_id   || '');
      }
    } else if (port.destination_type === 'uplink') {
      dest_type = 'uplink'; uplink_txt = port.connected_to || '';
    }

    setEditForm({
      ...EMPTY_FORM,
      destination_type:       dest_type,
      destination_outlet_id:  outlet_id,
      destination_element_id: elem_id,
      destination_port_id:    port_id,
      uplink_text:            uplink_txt,
      connection_type:  port.connection_type || 'rj45',
      cable_type:       conn?.cable_type  || port.cable_type  || '',
      cable_color:      conn?.cable_color || '',
      cable_length_m:   conn?.cable_length_m != null ? String(conn.cable_length_m) : '',
      notes:            conn?.notes       || port.notes       || '',
      vlan:  port.vlan  || '',
      speed: port.speed || '',
    });
  };

  const setDestType = (type) => {
    setEditForm(f => ({ ...f, destination_type: type, destination_outlet_id: '', destination_element_id: '', destination_port_id: '', uplink_text: '' }));
    setDestPorts([]);
  };

  // ── Save ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedPort) return;
    const myPortType   = isIPPhone ? 'ip_phone_port' : (isSwitch ? 'switch_port' : 'panel_port');
    const existingConn = selectedPort.connection;
    const dt           = editForm.destination_type;

    const connChanging = (() => {
      if (!existingConn && !dt) return false;
      if (!existingConn && dt)  return true;
      if (existingConn && !dt)  return true;
      if (existingConn && dt === 'uplink') return true;
      const other = existingConn.other_end;
      if (dt === 'wall') {
        const oid = parseInt(editForm.destination_outlet_id);
        return !(other.port_type === 'wall_outlet' && other.port_id === oid);
      }
      if (dt === 'element') return other.port_id !== parseInt(editForm.destination_port_id);
      return true;
    })();

    try {
      setSaving(true); setSaveError('');

      if (existingConn && connChanging)
        await api.deleteConnection(existingConn.id, companyId);

      if (dt === 'element' && editForm.destination_port_id) {
        const dp = destPorts.find(p => String(p.id) === editForm.destination_port_id);
        if (connChanging) {
          await api.createConnection({
            port_a_type: myPortType, port_a_id: selectedPort.id,
            port_b_type: dp?.port_entity_type || 'panel_port',
            port_b_id: parseInt(editForm.destination_port_id),
            cable_type: editForm.cable_type || undefined, cable_color: editForm.cable_color || undefined,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : undefined,
            notes: editForm.notes || undefined,
          }, companyId);
        } else if (existingConn) {
          await api.updateConnection(existingConn.id, {
            cable_type: editForm.cable_type, cable_color: editForm.cable_color,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : null,
            notes: editForm.notes,
          }, companyId);
        }
      } else if (dt === 'wall' && editForm.destination_outlet_id) {
        if (connChanging) {
          await api.createConnection({
            port_a_type: myPortType, port_a_id: selectedPort.id,
            port_b_type: 'wall_outlet', port_b_id: parseInt(editForm.destination_outlet_id),
            cable_type: editForm.cable_type || undefined, cable_color: editForm.cable_color || undefined,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : undefined,
            notes: editForm.notes || undefined,
          }, companyId);
        } else if (existingConn) {
          await api.updateConnection(existingConn.id, {
            cable_type: editForm.cable_type, cable_color: editForm.cable_color,
            cable_length_m: editForm.cable_length_m ? parseFloat(editForm.cable_length_m) : null,
            notes: editForm.notes,
          }, companyId);
        }
      }

      // For IP phones, we only manage connections, not port properties
      if (!isIPPhone) {
        const portPayload = { connection_type: editForm.connection_type, cable_type: editForm.cable_type, notes: editForm.notes, ...(isSwitch ? { vlan: editForm.vlan, speed: editForm.speed } : {}) };
        if (dt === 'uplink') { portPayload.destination_type = 'uplink'; portPayload.connected_to = editForm.uplink_text; portPayload.is_connected = Boolean(editForm.uplink_text); }
        else if (!dt) { portPayload.destination_type = null; portPayload.connected_to = null; }
        await api.updatePort(element.id, selectedPort.id, portPayload, companyId);
      }

      await fetchPorts(true);
      notifyUpdated();
      if (onClose) onClose();
    } catch (err) { setSaveError(err.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    const conn = selectedPort?.connection;
    if (!conn) return;
    try {
      setSaving(true); setSaveError('');
      await api.deleteConnection(conn.id, companyId);
      await fetchPorts(true);
      notifyUpdated();
      if (onClose) onClose();
      setEditForm(f => ({ ...f, destination_type: '', destination_outlet_id: '', destination_element_id: '', destination_port_id: '' }));
    } catch (err) { setSaveError(err.message || 'Error al desconectar'); }
    finally { setSaving(false); }
  };

  // ── Visual helpers ────────────────────────────────────────────────────

  const hasConnection = (p) => Boolean(p.connection);
  const hasUplink     = (p) => p.destination_type === 'uplink' && p.connected_to;

  const getPortStyle = (port) => {
    if (!port.is_active)     return { background: 'var(--port-inactive)', color: 'var(--port-inactive-text)' };
    if (hasConnection(port)) return { background: 'var(--port-patched)',  color: 'var(--port-patched-text)'  };
    if (hasUplink(port))     return { background: 'var(--port-pending)',  color: 'var(--port-pending-text)'  };
    return { background: 'var(--port-free)', color: 'var(--port-free-text)' };
  };

  const getPortLabel = (port) => {
    const conn = port.connection;
    if (conn) {
      const other = conn.other_end;
      if (other.port_type === 'wall_outlet') return '→W';
      return other.port_number != null ? `→${other.port_number}` : '→?';
    }
    if (hasUplink(port)) return '⬆';
    return '';
  };

  const CONNECTOR_MAP = {
    rj45:   { abbr: 'RJ45',  bg: 'var(--port-conn-default-bg)', fg: 'var(--port-conn-default-fg)' },
    lc:     { abbr: 'LC',    bg: 'var(--port-conn-fiber-bg)',   fg: 'var(--port-conn-fiber-fg)'   },
    sc:     { abbr: 'SC',    bg: 'var(--port-conn-fiber-bg)',   fg: 'var(--port-conn-fiber-fg)'   },
    st:     { abbr: 'ST',    bg: 'var(--port-conn-fiber-bg)',   fg: 'var(--port-conn-fiber-fg)'   },
    mtp:    { abbr: 'MTP',   bg: 'var(--port-conn-fiber-bg)',   fg: 'var(--port-conn-fiber-fg)'   },
    fc:     { abbr: 'FC',    bg: 'var(--port-conn-fiber-bg)',   fg: 'var(--port-conn-fiber-fg)'   },
    sfp:    { abbr: 'SFP',   bg: 'var(--port-conn-sfp-bg)',     fg: 'var(--port-conn-sfp-fg)'     },
    'sfp+': { abbr: 'SFP+',  bg: 'var(--port-conn-sfp-bg)',     fg: 'var(--port-conn-sfp-fg)'     },
    qsfp:   { abbr: 'QSFP',  bg: 'var(--port-conn-sfp-bg)',     fg: 'var(--port-conn-sfp-fg)'     },
  };
  const getConnector = (port) => {
    const ct = (port.connection_type || 'rj45').toLowerCase();
    return CONNECTOR_MAP[ct] || { abbr: ct.toUpperCase(), bg: 'var(--port-conn-default-bg)', fg: 'var(--port-conn-default-fg)' };
  };

  const getPortTitle = (port) => {
    const conn = port.connection;
    const vlan = port.vlan ? ` · ${vlanTitle(port.vlan)}` : '';
    if (conn) {
      const o = conn.other_end;
      let s = `P${port.port_number} → ${o.element_name || ''}`;
      if (o.port_type === 'wall_outlet') s += ` (${o.label || 'Toma'})`;
      else if (o.port_number != null)   s += ` P${o.port_number}`;
      if (conn.cable_type)  s += ` [${conn.cable_type}]`;
      if (conn.cable_color) s += ` ${conn.cable_color}`;
      return `${s}${vlan}`;
    }
    if (hasUplink(port)) return `P${port.port_number} ⬆ ${port.connected_to}${vlan}`;
    return `P${port.port_number} (libre)${vlan}`;
  };

  const totalPorts     = ports.length;
  const connectedPorts = ports.filter(p => hasConnection(p) || hasUplink(p)).length;
  const freePorts      = ports.filter(p => !hasConnection(p) && !hasUplink(p) && p.is_active).length;
  const PORTS_PER_ROW  = 12;
  const portRows = [];
  for (let i = 0; i < ports.length; i += PORTS_PER_ROW) portRows.push(ports.slice(i, i + PORTS_PER_ROW));

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="port-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="port-modal">

        <div className="port-modal-header">
          <div className="port-modal-title">
            <span className="port-modal-icon">{isIPPhone ? '☎️' : (isSwitch ? '🔌' : '🔗')}</span>
            <div>
              <h2>{element?.name || element?.code}</h2>
              <p>{isIPPhone ? 'Teléfono IP' : (isSwitch ? 'Switch de red' : 'Patch Panel')} · {totalPorts} puertos</p>
            </div>
          </div>
          <button className="port-modal-close" onClick={onClose} title="Cerrar (Esc)">✕</button>
        </div>

        <div className="port-modal-stats">
          <div className="port-stat"><span className="port-stat-dot" style={{ background: 'var(--port-patched)' }} /><strong>{connectedPorts}</strong> conectados</div>
          <div className="port-stat"><span className="port-stat-dot" style={{ background: 'var(--port-free)' }} /><strong>{freePorts}</strong> libres</div>
          <div className="port-stat port-stat--right">
            <div className="port-legend"><span style={{ background: 'var(--port-patched)' }} />Con conexión</div>
            <div className="port-legend"><span style={{ background: 'var(--port-pending)' }} />Uplink</div>
            <div className="port-legend"><span style={{ background: 'var(--port-free)' }} />Libre</div>
          </div>
        </div>

        <div className="port-modal-body">
          {/* Port Grid */}
          <div className="port-grid-section">
            <div className="port-grid-header">
              <span>Mapa de puertos</span>
              <span className="port-grid-hint">Haz clic para editar un puerto</span>
            </div>
            {loading ? (
              <div className="port-grid-loading">Cargando puertos...</div>
            ) : ports.length === 0 ? (
              <div className="port-grid-empty">Sin puertos configurados</div>
            ) : (
              <div className="port-grid">
                {portRows.map((row, ri) => (
                  <div key={ri} className="port-row">
                    {row.map(port => (
                      <button
                        key={port.id}
                        className={`port-btn${selectedPort?.id === port.id ? ' port-btn--selected' : ''}`}
                        style={getPortStyle(port)}
                        onClick={() => handlePortClick(port)}
                        title={getPortTitle(port)}
                      >
                        <span className="port-num">{port.port_number}</span>
                        {(() => { const c = getConnector(port); return <span className="port-connector-badge" style={{ background: c.bg, color: c.fg }}>{c.abbr}</span>; })()}
                        {getPortLabel(port) && <span className="port-dest-abbr">{getPortLabel(port)}</span>}
                        {isSwitch && vlanChip(port.vlan) && (
                          <span className={`port-vlan${vlanChip(port.vlan).trunk ? ' port-vlan--trunk' : ''}`}>
                            {vlanChip(port.vlan).label}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Port Editor */}
          {selectedPort && (
            <div className="port-editor">
              <div className="port-editor-header">
                <span className="port-editor-num">Puerto <strong>#{selectedPort.port_number}</strong></span>
                {selectedPort.label && <span className="port-editor-label">· {selectedPort.label}</span>}
                <span className={`port-editor-badge ${hasConnection(selectedPort) || hasUplink(selectedPort) ? 'badge--connected' : 'badge--free'}`}>
                  {hasConnection(selectedPort) ? '● Conectado' : hasUplink(selectedPort) ? '● Uplink' : '○ Libre'}
                </span>
              </div>

              {/* Active connection info */}
              {selectedPort.connection && (
                <div className="port-conn-info">
                  <div className="port-conn-path">
                    <span className="port-conn-arrow">⟶</span>
                    <span>
                      <strong>{selectedPort.connection.other_end?.element_name || '—'}</strong>
                      {selectedPort.connection.other_end?.port_type !== 'wall_outlet' && selectedPort.connection.other_end?.port_number != null &&
                        <> · Puerto {selectedPort.connection.other_end.port_number}</>}
                      {selectedPort.connection.other_end?.port_type === 'wall_outlet' &&
                        <> · Toma {selectedPort.connection.other_end.label}</>}
                      {selectedPort.connection.cable_type && <span className="port-conn-cable"> [{selectedPort.connection.cable_type}]</span>}
                      {selectedPort.connection.cable_color && <span className="port-conn-color"> 🎨 {selectedPort.connection.cable_color}</span>}
                    </span>
                  </div>
                  <button className="port-disconnect-btn" onClick={handleDisconnect} disabled={saving}>✂️ Desconectar</button>
                </div>
              )}

              {/* Destination type */}
              <div className="port-field">
                <label>Destino</label>
                <div className="port-dest-toggle">
                  <button className={`port-toggle-btn${editForm.destination_type === 'wall'    ? ' active' : ''}`} onClick={() => setDestType('wall')}>🏢 Toma de pared</button>
                  <button className={`port-toggle-btn${editForm.destination_type === 'element' ? ' active' : ''}`} onClick={() => setDestType('element')}>🔗 Elemento del rack</button>
                  {isSwitch && <button className={`port-toggle-btn${editForm.destination_type === 'uplink' ? ' active' : ''}`} onClick={() => setDestType('uplink')}>⬆ Uplink</button>}
                  <button className={`port-toggle-btn${!editForm.destination_type ? ' active' : ''}`} onClick={() => setDestType('')}>⬜ Sin destino</button>
                </div>
              </div>

              {/* Wall outlet selector */}
              {editForm.destination_type === 'wall' && (
                <div className="port-field">
                  <label>Toma de pared</label>
                  {loadingWallOutlets ? <span className="port-loading-hint">Cargando tomas...</span>
                  : wallOutlets.length === 0 ? <p className="port-empty-hint">Sin tomas registradas. Créalas en <em>Infraestructura → Cableado</em>.</p>
                  : (
                    <select className="port-select" value={editForm.destination_outlet_id} onChange={e => setEditForm(f => ({ ...f, destination_outlet_id: e.target.value }))}>
                      <option value="">— selecciona una toma —</option>
                      {wallOutlets.map(o => {
                        const occupied = o.connection && o.connection.id !== selectedPort.connection?.id;
                        return <option key={o.id} value={o.id} disabled={occupied}>{o.code}{o.name ? ` · ${o.name}` : ''}{o.room ? ` (${o.room})` : ''}{occupied ? ' 🔴' : ''}</option>;
                      })}
                    </select>
                  )}
                </div>
              )}

              {/* Element + port selector */}
              {editForm.destination_type === 'element' && (
                <>
                  <div className="port-field">
                    <label>Elemento destino</label>
                    <label
                      className="port-scope-toggle"
                      title="Por defecto se muestran los equipos de la misma raíz (sede/edificio). Marca para ver también otras ubicaciones."
                    >
                      <input
                        type="checkbox"
                        checked={showAllLocations}
                        onChange={e => setShowAllLocations(e.target.checked)}
                      />
                      <span>Mostrar elementos de todas las ubicaciones</span>
                      <em>{otherElements.length} destinos</em>
                    </label>
                    <select className="port-select" value={editForm.destination_element_id} onChange={e => setEditForm(f => ({ ...f, destination_element_id: e.target.value, destination_port_id: '' }))}>
                      <option value="">— selecciona un elemento —</option>
                      {otherElementGroups.map(grupo => (
                        <React.Fragment key={grupo.key}>
                          {/* Título de grupo como option disabled: <optgroup>
                              se pinta con fondo blanco en Windows y rompe el tema. */}
                          <option value={`__grupo-${grupo.key}`} disabled className="port-select-group">
                            ── {grupo.label} ──
                          </option>
                          {grupo.elements.map(el => (
                            <option key={el.id} value={el.id}>[{el.element_type}] {el.name || el.code}</option>
                          ))}
                        </React.Fragment>
                      ))}
                      {!otherElementGroups.length && (
                        <option value="" disabled>
                          No hay elementos en esta ubicación; marca «Mostrar todas las ubicaciones»
                        </option>
                      )}
                    </select>
                  </div>
                  {editForm.destination_element_id && (
                    <div className="port-field">
                      <label>Puerto de destino</label>
                      {loadingDestPorts ? <span className="port-loading-hint">Cargando puertos...</span>
                      : destPorts.length === 0 ? <p className="port-empty-hint">Sin puertos configurados.</p>
                      : (
                        <select className="port-select" value={editForm.destination_port_id} onChange={e => setEditForm(f => ({ ...f, destination_port_id: e.target.value }))}>
                          <option value="">— selecciona un puerto —</option>
                          {destPorts.map(p => {
                            const occupied = p.connection && p.id !== parseInt(editForm.destination_port_id);
                            const label = p.component_name ? `${p.component_name} · P${p.port_number}${p.label ? ` (${p.label})` : ''}${p.speed ? ` ${p.speed}` : ''}` : `P${p.port_number}${p.label ? ` · ${p.label}` : ''}`;
                            return <option key={p.id} value={p.id} disabled={occupied}>{label} {occupied ? '🔴 ocupado' : '🟢'}</option>;
                          })}
                        </select>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Uplink free text */}
              {editForm.destination_type === 'uplink' && (
                <div className="port-field">
                  <label>Descripción uplink</label>
                  <input className="port-input" placeholder="Ej: Core SW-01 port 48, ISP router..." value={editForm.uplink_text} onChange={e => setEditForm(f => ({ ...f, uplink_text: e.target.value }))} />
                </div>
              )}

              {/* Cable details */}
              <div className="port-fields-grid">
                <div className="port-field">
                  <label>Conector</label>
                  <select className="port-select" value={editForm.connection_type} onChange={e => setEditForm(f => ({ ...f, connection_type: e.target.value }))}>
                    {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="port-field">
                  <label>Tipo de cable</label>
                  <select className="port-select" value={editForm.cable_type} onChange={e => setEditForm(f => ({ ...f, cable_type: e.target.value }))}>
                    {CABLE_TYPES.map(t => <option key={t} value={t}>{t || '— sin especificar —'}</option>)}
                  </select>
                </div>
                <div className="port-field">
                  <label>Color cable</label>
                  <select className="port-select" value={editForm.cable_color} onChange={e => setEditForm(f => ({ ...f, cable_color: e.target.value }))}>
                    {CABLE_COLORS.map(c => <option key={c} value={c}>{c || '— sin color —'}</option>)}
                  </select>
                </div>
                <div className="port-field">
                  <label>Longitud (m)</label>
                  <input className="port-input" type="number" min="0" step="0.5" placeholder="0.5" value={editForm.cable_length_m} onChange={e => setEditForm(f => ({ ...f, cable_length_m: e.target.value }))} />
                </div>
                {isSwitch && (<>
                  <div className="port-field">
                    <label>VLAN</label>
                    <input className="port-input" placeholder="100, trunk..." value={editForm.vlan} onChange={e => setEditForm(f => ({ ...f, vlan: e.target.value }))} />
                  </div>
                  <div className="port-field">
                    <label>Velocidad</label>
                    <select className="port-select" value={editForm.speed} onChange={e => setEditForm(f => ({ ...f, speed: e.target.value }))}>
                      {SWITCH_SPEEDS.map(s => <option key={s} value={s}>{s || '— auto —'}</option>)}
                    </select>
                  </div>
                </>)}
              </div>

              <div className="port-field">
                <label>Notas</label>
                <input className="port-input" placeholder="Notas opcionales..." value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
              </div>

              {saveError && <div className="port-error">{saveError}</div>}
              <div className="port-editor-actions">
                <button className="port-save-btn" onClick={handleSave} disabled={saving}>
                  {saving ? '⏳ Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PortMapModal;
